import { Box, Button, Chip, Divider, IconButton, Popover, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState } from 'react';
import { TbBookmark, TbDeviceFloppy } from 'react-icons/tb';
import { IFollowupFilterPreference, IPreferenceSummaryItem, formatSavedAt } from './followupFilterPreference';

interface IProps {
  preference: IFollowupFilterPreference | null;
  isDirty: boolean;
  summary: IPreferenceSummaryItem[];
  onSave: () => void;
}

const LABEL_WIDTH = 104;

const FollowupFilterPreferenceControls = (props: IProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { preference, isDirty, summary } = props;
  const hasPreference = !!preference;

  const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation();

  const chipStyle = isDirty
    ? {
      backgroundColor: alpha(theme.palette.warning.main, 0.12),
      color: theme.palette.warning.dark,
      border: `1px solid ${alpha(theme.palette.warning.main, 0.24)}`,
    }
    : {
      backgroundColor: alpha(theme.palette.success.main, 0.12),
      color: theme.palette.success.dark,
      border: `1px solid ${alpha(theme.palette.success.main, 0.24)}`,
    };

  const handleSave = () => {
    props.onSave();
    setAnchorEl(null);
  };

  return (
    <>
      {hasPreference && (
        <Chip
          size="small"
          icon={<TbBookmark size={14} style={{ marginLeft: 6 }} />}
          label={isDirty ? 'Filtros alterados' : 'Preferência aplicada'}
          sx={{
            ...chipStyle,
            height: 22,
            fontSize: '0.75rem',
            fontWeight: 500,
            '& .MuiChip-icon': { color: 'inherit' },
          }}
        />
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Box
        onClick={stopPropagation}
        onFocus={stopPropagation}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        <Tooltip title={hasPreference && !isDirty ? 'Os filtros atuais já estão salvos' : ''} placement="top">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<TbDeviceFloppy />}
              disabled={hasPreference && !isDirty}
              onClick={handleSave}
            >
              {hasPreference ? 'Atualizar preferência' : 'Salvar filtros'}
            </Button>
          </span>
        </Tooltip>

        {hasPreference && (
          <IconButton
            size="small"
            aria-label="Preferência de filtros"
            onClick={event => setAnchorEl(event.currentTarget)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}

        <Divider orientation="vertical" flexItem sx={{ ml: 0.5, my: 0.75 }} />
      </Box>

      <Popover
        open={!!anchorEl && hasPreference}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        elevation={2}
        PaperProps={{
          sx: {
            width: 320,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Filtro salvo
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {preference?.savedAt ? `Salvo em ${formatSavedAt(preference.savedAt)}` : 'Sem data de gravação'}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1 }}>
          {summary.length ? (
            summary.map(item => (
              <Box
                key={item.label}
                sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.5 }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', width: LABEL_WIDTH, flexShrink: 0 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  title={item.value}
                  sx={{ flex: 1, minWidth: 0, textAlign: 'right', fontWeight: 500 }}
                >
                  {item.value}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Nenhum filtro preenchido na preferência salva.
            </Typography>
          )}
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.5 }}>
          <Button variant="outlined" size="small" fullWidth disabled={!isDirty} onClick={handleSave}>
            Atualizar com filtros atuais
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default FollowupFilterPreferenceControls;
